import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Plus,
  Download,
  ToggleLeft,
  ToggleRight,
  FilePenLine,
  Wallet2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import constant from "../../constant";
import StatsBox from "../common/statsBox";
import FileSaver from "file-saver";
import Papa from "papaparse";

const SalesTable = ({
  sales: propSales,
  onDelete,
  onEdit,
  selectedPeriod,
  setSelectedPeriod,
  salesFreezePeriodStart,
  salesFreezePeriodEnd,
}) => {
  const [localSales, setLocalSales] = useState(propSales);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeStatusFilter, setActiveStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const userRole = localStorage.getItem("role");
  const [downloadLoading, setDownloadLoading] = useState(false); // Loading state

  const [totalSalesStat, setTotalSalesStat] = useState(0);
  const [paidSalesStat, setPaidSalesStat] = useState(0);
  const [unpaidSalesStat, setUnpaidSalesStat] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedSaleIdForPayment, setSelectedSaleIdForPayment] =
    useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Add state for sorting
  const [sortConfig, setSortConfig] = useState({
    key: "invoiceDate", // Default sort by invoiceDate
    direction: "desc", // Default sort direction
  });

  // console.log(
  //   "salesFreezePeriodStart",
  //   salesFreezePeriodStart,
  //   "salesFreezePeriodEnd",
  //   salesFreezePeriodEnd
  // );
  useEffect(() => {
    setLocalSales(propSales);
  }, [propSales]);

  useEffect(() => {
    if (paymentSuccess) {
      window.location.reload();
      setPaymentSuccess(false);
      setIsPaymentModalOpen(false);
      setSelectedSaleIdForPayment(null);

      if (selectedSaleIdForPayment) {
        onEdit(selectedSaleIdForPayment, null, true);
      }
    }
  }, [paymentSuccess, onEdit, selectedSaleIdForPayment]);

  const constructApiUrl = (endpoint) => {
    const userRole = localStorage.getItem("role");
    const adminSelectedUser = localStorage.getItem("adminSelectedUser");
    let apiUrl = `${constant.REACT_APP_API_URL}${endpoint}`;

    // Initialize query parameters array
    const queryParams = [];

    // Add userId if admin and adminSelectedUser exists
    if (userRole === "admin" && adminSelectedUser) {
      queryParams.push(`userId=${adminSelectedUser}`);
    }

    // Add date filters if selectedPeriod.from and selectedPeriod.to exist
    if (
      endpoint === "/sales/download" &&
      selectedPeriod?.from &&
      selectedPeriod?.to
    ) {
      const formattedStartDate = selectedPeriod.from
        .toLocaleDateString("en-GB")
        .split("/")
        .join("-");
      const formattedEndDate = selectedPeriod.to
        .toLocaleDateString("en-GB")
        .split("/")
        .join("-");
      queryParams.push(`startDate=${formattedStartDate}`);
      queryParams.push(`endDate=${formattedEndDate}`);
    }

    // Append query parameters to URL if any exist
    if (queryParams.length > 0) {
      apiUrl += `?${queryParams.join("&")}`;
    }

    return apiUrl;
  };

  useEffect(() => {
    const fetchDashboardStats = async () => {
      setStatsLoading(true);
      setStatsError(null);
      const token = localStorage.getItem("token");
      if (!token) {
        setStatsError("No authentication token found");
        setStatsLoading(false);
        return;
      }

      const apiUrl = constructApiUrl("/sales/dashboard");

      try {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const message = await response.json();
          throw new Error(
            message.message || `HTTP error! status: ${response.status}`
          );
        }

        const result = await response.json();

        setTotalSalesStat(Math.ceil(result.data.totalSales));
        setUnpaidSalesStat(result.data.unpaid);
        setPaidSalesStat(
          parseFloat(Math.ceil(result.data.totalSales)) -
            parseFloat(result.data.unpaid)
        );
      } catch (error) {
        setStatsError("Error fetching dashboard stats: " + error.message);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  const handleToggleStatus = async (saleId, currentStatus) => {
    setLocalSales((prevSales) =>
      prevSales.map((sale) =>
        sale._id === saleId ? { ...sale, isActive: !currentStatus } : sale
      )
    );

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found");
        return;
      }

      const response = await fetch(
        `${constant.REACT_APP_API_URL}/sales/${saleId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isActive: !currentStatus }),
        }
      );

      if (response.ok) {
        onEdit(saleId, !currentStatus);
      } else {
        setLocalSales((prevSales) =>
          prevSales.map((sale) =>
            sale._id === saleId ? { ...sale, isActive: currentStatus } : sale
          )
        );
        const result = await response.json();
        throw new Error(result.message || "Failed to toggle sale status");
      }
    } catch (error) {
      console.error(error);
      alert(error.message);
    }
  };

  const handleFromDateChange = (e) => {
    setSelectedPeriod((prev) => ({ ...prev, from: new Date(e.target.value) }));
  };

  const handleToDateChange = (e) => {
    setSelectedPeriod((prev) => ({ ...prev, to: new Date(e.target.value) }));
  };

  // Sorting function
  const sortSales = (sales, key, direction) => {
    return [...sales].sort((a, b) => {
      if (key === "invoiceDate") {
        const dateA = a.invoiceDate ? new Date(a.invoiceDate) : new Date(0);
        const dateB = b.invoiceDate ? new Date(b.invoiceDate) : new Date(0);
        return direction === "asc" ? dateA - dateB : dateB - dateA;
      } else if (key === "invoiceNumber") {
        const numA = a.invoiceNumber || "";
        const numB = b.invoiceNumber || "";
        return direction === "asc"
          ? numA.localeCompare(numB)
          : numB.localeCompare(numA);
      }
      return 0;
    });
  };

  // Handle sort click
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // Apply sorting to filtered sales
  const filteredSales = sortSales(
    localSales.filter((sale) => {
      if (!sale) return false;
      const matchesStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "paid"
          ? sale.paymentStatus === "paid"
          : sale.paymentStatus === "unpaid";

      const matchesActiveStatus =
        activeStatusFilter === "all"
          ? true
          : activeStatusFilter === "active"
          ? sale.isActive
          : !sale.isActive;

      const matchesSearch =
        sale.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (sale.partyData &&
          sale.partyData.name
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()));

      return matchesStatus && matchesSearch && matchesActiveStatus;
    }),
    sortConfig.key,
    sortConfig.direction
  );

  const handleDownload = async () => {
    const token = localStorage.getItem("token");
    const userRole = localStorage.getItem("role");
    const adminSelectedUser = localStorage.getItem("adminSelectedUser");

    if (!token) {
      alert("No authentication token found");
      return;
    }

    let apiUrl = constructApiUrl("/sales/download");

    setDownloadLoading(true);

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();

        if (result) {
          const csvData = convertJsonToCsv(result);

          const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
          const now = new Date();
          const pad = (n) => n.toString().padStart(2, "0");
          const dateStr = `${now.getFullYear()}-${pad(
            now.getMonth() + 1
          )}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(
            now.getMinutes()
          )}-${pad(now.getSeconds())}`;
          FileSaver.saveAs(blob, `sales_${dateStr}.csv`);
        } else {
          alert("No data received for download.");
        }
      } else {
        const errorResult = await response.json();
        throw new Error(errorResult.message || "Failed to download sales");
      }
    } catch (error) {
      console.error("Download error:", error);
      alert(error.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  const convertJsonToCsv = (jsonData) => {
    const csvData = jsonData.map((item) => {
      const format = (val) =>
        val !== undefined && val !== null && !isNaN(val)
          ? `="${parseFloat(val).toFixed(2)}"` // Excel will treat as text
          : val;
    
      // Initialize all GST columns to empty
      let gstColumns = {
        "IGST LED 5%": "",
        "IGST AMT 5%": "",
        "IGST LED 12%": "",
        "IGST AMT 12%": "",
        "IGST LED 18%": "",
        "IGST AMT 18%": "",
        "IGST LED 28%": "",
        "IGST AMT 28%": "",
        "CGST LED 2.5%": "",
        "SGST LED 2.5%": "",
        "CGST AMT 2.5%": "",
        "SGST AMT 2.5%": "",
        "CGST LED 6%": "",
        "SGST LED 6%": "",
        "CGST AMT 6%": "",
        "SGST AMT 6%": "",
        "CGST LED 9%": "",
        "SGST LED 9%": "",
        "CGST AMT 9%": "",
        "SGST AMT 9%": "",
        "CGST LED 14%": "",
        "SGST LED 14%": "",
        "CGST AMT 14%": "",
        "SGST AMT 14%": "",
      };

      // Parse numeric tax rate
      const rate = parseFloat(item.taxRate);

      // Assign based on tax rate slab
      switch (rate) {
        case 5:
          gstColumns["CGST LED 2.5%"] = "OUTPUT CGST @ 2.5%";
          gstColumns["SGST LED 2.5%"] = "OUTPUT SGST @ 2.5%";
          gstColumns["CGST AMT 2.5%"] = format(item.CGST);
            gstColumns["SGST AMT 2.5%"] = format(item.SGST);
          break;
        case 12:
          gstColumns["CGST LED 6%"] = "OUTPUT CGST @ 6%";
          gstColumns["SGST LED 6%"] = "OUTPUT SGST @ 6%";
            gstColumns["CGST AMT 6%"] = format(item.CGST);
            gstColumns["SGST AMT 6%"] = format(item.SGST);
            break;
          case 18:
            gstColumns["CGST LED 9%"] = "OUTPUT CGST @ 9%";
            gstColumns["SGST LED 9%"] = "OUTPUT SGST @ 9%";
            gstColumns["CGST AMT 9%"] = format(item.CGST);
            gstColumns["SGST AMT 9%"] = format(item.SGST);
            break;
          case 28:
            gstColumns["CGST LED 14%"] = "OUTPUT CGST @ 14%";
            gstColumns["SGST LED 14%"] = "OUTPUT SGST @ 14%";
            gstColumns["CGST AMT 14%"] = format(item.CGST);
            gstColumns["SGST AMT 14%"] = format(item.SGST);
          break;
        default:
          // No match
          break;
      }

      return {
        "Voucher Date": item.invoiceDate,
        "Voucher Type Name": "Sales",
        "Voucher Number": item.invoiceNumber,
        "Order Number": "",
        "Order Date": "",
        "Ledger Name": `Local Sale @ ${item.taxRate}%`,
        "Item Amount": format(item.itemAmount),
        "Party Name": item.partyName,
        "Ledger Amount": format(item.totalAmount),
        "Rate of tax": item.taxRate,
        Credit: "Cr",
        ...gstColumns,
        "Other ledgers1": "",
        "other ledger amount1": "",
        "Other ledgers2": "",
        "other ledger amount2": "",
        "EXPENSE LED WITHOUT GST": "",
        "EXPENSE AMT WITHOUT GST": "",
        "Item Name": item.itemName,
        "Item Description": item.description,
        "Billed Quantity": format(item.quantity),
        mfg: "",
        batch: "",
        exp: "",
        mrp: "",
        hsn: format(item.hsnCode),
        "Item Rate": format(item.rate),
        "Item Rate per": item.unit,
        Debit: "Dr",
        NARRATION: "dd",
        "change mode": "item invoice",
        "Mode/Terms of payment": "",
        "Other References": "",
        "Terms of Delivery": "",
        "buyer(Bill to)": item.billingAddressName,
        "BUYER/SUPPLIER-MAILING NAME": item.billingAddressName,
        "BUYER/SUPPLIER ADDRESS":
          item.billingAddressStreet + ", " + item.billingAddressCity,
        "BUYER/SUPPLIER - STATE": item.billingAddressState,
        "BUYER/SUPPLIER GST REGISTRATION TYPE": "Regular",
        "Buyer/Supplier GSTIN Number": item.billingAddressGST,
        "consighnee (ship to)": item.shippingAddressName,
        "Mailing Name (ship to)": item.shippingAddressName,
        "address (ship to)":
          item.shippingAddressStreet + ", " + item.shippingAddressCity,
        "state (ship to)": item.shippingAddressState,
        "Gstin/Uin (shi to)": item.shippingAddressGST,
        "Delivery Note NO(s)": "",
        "Dispatch Doc No.": "",
        "Dispactched through": "",
        Destination: "",
        "Carrier Name/Agent": "",
        "AWB No./LR-RR No.": "",
        "BL date": "",
        "Motor Vechicle No.": "",
        "Place of Receipt by shipper": "",
        "shipping Bill No-": "",
        "date of shipping Bill No.": "",
        "Vessel/Flight No": "",
        "Port of Loading": "",
        "Port of Discharge": "",
        "Country To": item.shippingAddressCountry,
        "Port Code": "",
        "Place of Supply": item.partyState,
        "eway bill number": "",
        "eway bill date": "",
        state: item.partyState,
        PORT: "",
        "COUNTRY NAME": item.billingAddressCountry,
      };
    });

    // Define header order
    const fields = Object.keys(csvData[0]);

    const csv = Papa.unparse({
      fields,
      data: csvData,
    });

    return csv;
  };

  const openPaymentModal = (saleId) => {
    setSelectedSaleIdForPayment(saleId);
    setIsPaymentModalOpen(true);
    setPaymentError(null);
  };

  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedSaleIdForPayment(null);
    setPaymentError(null);
  };

  const handleRecordPayment = async (paymentData) => {
    setPaymentError(null);
    const token = localStorage.getItem("token");
    if (!token) {
      setPaymentError("No authentication token found.");
      return;
    }

    if (!selectedSaleIdForPayment) {
      setPaymentError("Sale ID is missing.");
      return;
    }

    try {
      const response = await fetch(
        `${constant.REACT_APP_API_URL}/ledger/invoice/${selectedSaleIdForPayment}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(paymentData),
        }
      );

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || "Failed to record payment");
      }

      setPaymentSuccess(true);
    } catch (error) {
      console.error("Record payment error:", error);
      setPaymentError(error.message);
    }
  };

  return (
    <div className="max-w-8xl mx-auto px-0 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sales</h1>
          </div>
          <div className="flex gap-3">
            {userRole === "admin" ? (
              <button
                onClick={handleDownload}
                disabled={downloadLoading}
                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
              >
                {downloadLoading ? (
                  <>
                    <Download size={18} className="mr-2" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download size={18} className="mr-2" />
                    Download
                  </>
                )}
              </button>
            ) : (
              <Link
                to="/sales/add"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
              >
                <Plus size={18} className="mr-2" />
                Add Sale
              </Link>
            )}
          </div>
        </div>

        <div className="grid grid-cols-12 md:gap-6 gap-4 my-4">
          <StatsBox
            title="Total Sales"
            text={statsLoading ? "Loading..." : totalSalesStat}
            bg="purple"
          />
          <StatsBox
            title="Paid"
            text={statsLoading ? "Loading..." : paidSalesStat}
            bg="green"
          />
          <StatsBox
            title="Unpaid"
            text={statsLoading ? "Loading..." : unpaidSalesStat}
            bg="red"
          />
        </div>

        {statsError && <div className="text-red-500">{statsError}</div>}

        <div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center w-full md:w-auto">
              <div className="relative w-50">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Search sales..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-50 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
                />
              </div>
              <div className="flex gap-2">
                {["all", "paid", "unpaid"].map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`
                      px-4 py-2 rounded-md text-xs font-medium transition-all duration-150
                      ${
                        statusFilter === status
                          ? "bg-blue-50 text-blue-700 border-2 border-blue-200"
                          : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-50"
                      }
                    `}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
                {["all", "active", "inactive"].map((status) => (
                  <button
                    key={status}
                    onClick={() => setActiveStatusFilter(status)}
                    className={`
                      px-4 py-2 rounded-md text-xs font-medium transition-all duration-150
                      ${
                        activeStatusFilter === status
                          ? "bg-blue-50 text-blue-700 border-2 border-blue-200"
                          : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-50"
                      }
                    `}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium">From:</label>
                <input
                  type="date"
                  // value={selectedPeriod.from.toISOString().split("T")[0]}
                  onChange={handleFromDateChange}
                  className="border rounded px-2 py-1 text-xs"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium">To:</label>
                <input
                  type="date"
                  // value={selectedPeriod.to.toISOString().split("T")[0]}
                  onChange={handleToDateChange}
                  className="border rounded px-2 py-1 text-xs"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
        <div className="min-w-full overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("invoiceDate")}
                >
                  <div className="flex items-center">
                    Invoice Date
                    {sortConfig.key === "invoiceDate" &&
                      (sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} className="ml-1" />
                      ) : (
                        <ArrowDown size={14} className="ml-1" />
                      ))}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("invoiceNumber")}
                >
                  <div className="flex items-center">
                    Invoice Number
                    {sortConfig.key === "invoiceNumber" &&
                      (sortConfig.direction === "asc" ? (
                        <ArrowUp size={14} className="ml-1" />
                      ) : (
                        <ArrowDown size={14} className="ml-1" />
                      ))}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Party Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mobile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSales.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-4 whitespace-nowrap text-center"
                  >
                    No Data Found
                  </td>
                </tr>
              ) : (
                filteredSales.map((sale) => {
                  if (!sale) return null;
                  const isDueDateOverdue = new Date(sale.dueDate) < new Date();
                  return (
                    <tr
                      key={sale._id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                        <Link
                          to={`/invoice/${sale._id}`}
                          target="_blank"
                          className="flex items-center"
                        >
                          {sale.invoiceDate
                            ? new Date(sale.invoiceDate).toLocaleDateString()
                            : "N/A"}
                        </Link>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/invoice/${sale._id}`}
                          className="flex items-center"
                        >
                          <span
                            className={`text-xs font-medium ${
                              isDueDateOverdue && !sale.fullyPaid
                                ? "text-red-600"
                                : "text-gray-900"
                            } hover:text-blue-600`}
                          >
                            {sale.invoiceNumber}
                          </span>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 capitalize">
                        {sale.partyData?.name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                        {sale.partyData?.mobile || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                        ₹{" "}
                        {parseInt(sale.totalAmount.toFixed(2)) +
                          parseInt(sale.roundoff.toFixed(2))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                        {sale.dueDate
                          ? new Date(sale.dueDate).toLocaleDateString()
                          : "N/A"}
                        {isDueDateOverdue &&
                          sale.dueDate &&
                          sale.paymentStatus !== "paid" && (
                            <div className="text-red-500 mt-1 text-sm">
                              Overdue
                            </div>
                          )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {sale.fullyPaid ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <span className="text-green-600">Paid</span>
                          </span>
                        ) : sale.amountReceived > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <span className="text-yellow-600">
                              Partially Paid
                            </span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <span className="text-red-600">Unpaid</span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                        <div className="flex items-center space-x-3">
                          {userRole === "admin" ? (
                            ""
                          ) : (
                            <>
                              {sale.invoiceDate &&
                              new Date(sale.invoiceDate) >=
                                new Date(salesFreezePeriodStart) &&
                              new Date(sale.invoiceDate) <=
                                new Date(salesFreezePeriodEnd) ? (
                                <>
                                  <FilePenLine
                                    size={18}
                                    className="text-gray-400 cursor-not-allowed"
                                  />
                                  <ToggleRight
                                    size={24}
                                    className="text-gray-400 cursor-not-allowed"
                                  />
                                </>
                              ) : (
                                <>
                                  {!sale.isActive ? (
                                    <FilePenLine
                                      size={18}
                                      className="text-gray-400 cursor-not-allowed"
                                    />
                                  ) : (
                                    <Link
                                      to={`/sales/edit/${sale._id}`}
                                      className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                                    >
                                      <FilePenLine size={18} />
                                    </Link>
                                  )}

                                  <button
                                    onClick={() =>
                                      handleToggleStatus(
                                        sale._id,
                                        sale.isActive
                                      )
                                    }
                                    className={
                                      sale.isActive
                                        ? "text-gray-600 hover:text-gray-900"
                                        : "text-red-600 hover:text-red-800"
                                    }
                                  >
                                    {sale.isActive ? (
                                      <ToggleRight size={24} />
                                    ) : (
                                      <ToggleLeft size={24} />
                                    )}
                                  </button>
                                </>
                              )}
                              {sale.fullyPaid || !sale.isActive ? (
                                <Wallet2 size={18} className="text-gray-500" />
                              ) : (
                                <button
                                  onClick={() => openPaymentModal(sale._id)}
                                  className="text-green-600 hover:text-green-900 transition-colors duration-150"
                                >
                                  <Wallet2 size={18} />
                                </button>
                              )}
                            </>
                          )}

                          <Link
                            to={`/invoice/${sale._id}`}
                            className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                            target="_blank"
                          >
                            <Download size={18} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isPaymentModalOpen && (
        <PaymentModal
          saleId={selectedSaleIdForPayment}
          totalAmount={
            filteredSales.find((sale) => sale._id === selectedSaleIdForPayment)
              ?.pendingAmount || 0
          }
          onClose={closePaymentModal}
          onRecordPayment={handleRecordPayment}
          error={paymentError}
        />
      )}
    </div>
  );
};

const PaymentModal = ({
  saleId,
  totalAmount,
  onClose,
  onRecordPayment,
  error,
}) => {
  const [amount, setAmount] = useState(totalAmount);
  const [amountInitial, setAmountInitial] = useState(totalAmount);
  const [transactionDate, setTransactionDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [description, setDescription] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const paymentData = {
      amount: parseFloat(amount),
      transactionDate: transactionDate,
      description: description,
      paymentMode: paymentMode,
    };
    await onRecordPayment(paymentData);
    setLoading(false);
  };

  return (
    <div
      className="fixed z-10 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        ></span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3
              className="text-lg font-medium leading-6 text-gray-900"
              id="modal-title"
            >
              Record Payment
            </h3>
            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            <form onSubmit={handleSubmit} className="mt-4">
              <div className="mb-4">
                <label
                  htmlFor="amount"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Amount
                </label>
                <input
                  type="number"
                  id="amount"
                  placeholder="Amount"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={amount}
                  max={amountInitial}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="transactionDate"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Transaction Date
                </label>
                <input
                  type="date"
                  id="transactionDate"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="paymentMode"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Payment Mode
                </label>
                <select
                  id="paymentMode"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                >
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="bank">Bank</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="mb-4">
                <label
                  htmlFor="description"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Description (Optional)
                </label>
                <textarea
                  id="description"
                  rows="3"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                ></textarea>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={loading}
                >
                  {loading ? "Recording..." : "Record Payment"}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesTable;
